import { concepts, predicates, researchSeededAt, researchSources, seedClaims } from '../data/researchDemo'
import type {
  Claim,
  ClaimStatus,
  ProposalDraft,
  ResearchAction,
  ResearchHistoryEvent,
  ResearchState,
  ReviewDraft,
  ReviewOutcome,
} from '../types/researchDemo'
import { formatId, nowIso } from './demoStorage'

export const researchStorageKey = 'noetivra.demo.research.v1'

export const statusLabels: Record<ClaimStatus, string> = {
  proposed: 'Proposed',
  approved: 'Approved',
  'revision-requested': 'Revision requested',
  rejected: 'Rejected',
}

export const outcomeLabels: Record<ReviewOutcome, string> = {
  approved: 'Approved',
  'revision-requested': 'Revision requested',
  rejected: 'Rejected',
}

export const sourceById = (id: string | null | undefined) => researchSources.find((source) => source.id === id)
export const conceptById = (id: string) => concepts.find((concept) => concept.id === id)
export const predicateLabel = (id: string) => predicates.find((predicate) => predicate.id === id)?.label ?? id
export const passageFor = (sourceId: string, passageId: string) =>
  sourceById(sourceId)?.passages.find((passage) => passage.id === passageId)

export const claimSummary = (claim: Claim) =>
  `${conceptById(claim.subjectId)?.label ?? claim.subjectId} → ${predicateLabel(claim.predicate)} → ${
    conceptById(claim.objectId)?.label ?? claim.objectId
  }`

export const emptyProposal: ProposalDraft = {
  subjectId: '',
  predicate: '',
  objectId: '',
  sourceId: '',
  passageId: '',
  interpretation: '',
  limitation: '',
}

export function defaultReviewDraft(claim: Claim): ReviewDraft {
  return {
    outcome: '',
    rationale: '',
    interpretation: claim.interpretation,
    limitation: claim.limitation,
    checkedPassage: false,
    checkedLimits: false,
  }
}

export function validateProposal(draft: ProposalDraft) {
  const errors: Partial<Record<keyof ProposalDraft, string>> = {}
  const subject = conceptById(draft.subjectId)
  const object = conceptById(draft.objectId)
  if (!subject) errors.subjectId = 'Choose a controlled concept.'
  if (!object) errors.objectId = 'Choose a controlled concept.'
  if (subject && object && subject.id === object.id) errors.objectId = 'Subject and object must be different concepts.'
  if (!predicates.some((predicate) => predicate.id === draft.predicate)) errors.predicate = 'Choose an available predicate.'
  if (!sourceById(draft.sourceId)) errors.sourceId = 'Choose a source document.'
  else if (!passageFor(draft.sourceId, draft.passageId)) errors.passageId = 'Choose a passage from the selected source.'
  if (!draft.interpretation.trim()) errors.interpretation = 'Describe what the passage supports.'
  if (!draft.limitation.trim()) errors.limitation = 'State the population, context and limitations.'
  return errors
}

export function validateReview(draft: ReviewDraft) {
  const errors: Partial<Record<'outcome' | 'rationale' | 'checks' | 'interpretation' | 'limitation', string>> = {}
  if (!draft.outcome) errors.outcome = 'Choose a review outcome.'
  if (!draft.rationale.trim()) errors.rationale = 'A written rationale is required for every review.'
  if (!draft.interpretation.trim()) errors.interpretation = 'The interpretation cannot be empty.'
  if (!draft.limitation.trim()) errors.limitation = 'The limitations cannot be empty.'
  if (draft.outcome === 'approved' && !(draft.checkedPassage && draft.checkedLimits)) {
    errors.checks = 'Confirm both evidence checks before approving.'
  }
  return errors
}

export function createInitialResearchState(): ResearchState {
  const history: ResearchHistoryEvent[] = [
    {
      id: 'EVT-001',
      at: researchSeededAt,
      kind: 'review',
      title: 'REL-001 approved · sample record',
      detail: `${claimSummary(seedClaims[0])} · SRC-001 P1 · version 1`,
      claimId: 'REL-001',
    },
    ...seedClaims.slice(1).map((claim, index) => ({
      id: formatId('EVT', index + 2),
      at: researchSeededAt,
      kind: 'proposal' as const,
      title: `${claim.id} proposed · sample record`,
      detail: `${claimSummary(claim)} · ${claim.sourceId} ${claim.passageId}`,
      claimId: claim.id,
    })),
  ].reverse()

  return {
    claims: seedClaims.map((claim) => ({ ...claim })),
    notes: [],
    history,
    counters: { claim: seedClaims.length, note: 0, event: history.length, review: 0 },
    ui: {
      workspace: null,
      lastSection: { research: 'overview', curation: 'queue' },
      selectedSourceId: null,
      highlightPassageId: null,
      openClaimId: null,
      compareIds: ['SRC-001', 'SRC-002'],
      search: '',
      queueFilter: 'all',
      proposalDraft: { ...emptyProposal },
      noteDraft: { sourceId: 'SRC-001', text: '' },
      reviewDrafts: {},
    },
  }
}

function addEvent(state: ResearchState, event: Omit<ResearchHistoryEvent, 'id' | 'at'>, at: string) {
  const next = state.counters.event + 1
  return {
    history: [{ ...event, id: formatId('EVT', next), at }, ...state.history],
    counters: { ...state.counters, event: next },
  }
}

export function researchReducer(state: ResearchState, action: ResearchAction): ResearchState {
  const ui = state.ui
  switch (action.type) {
    case 'enterWorkspace':
      return { ...state, ui: { ...ui, workspace: action.workspace, openClaimId: null } }
    case 'setSection':
      if (!ui.workspace) return state
      return {
        ...state,
        ui: { ...ui, openClaimId: null, lastSection: { ...ui.lastSection, [ui.workspace]: action.section } },
      }
    case 'goTo':
      return {
        ...state,
        ui: {
          ...ui,
          workspace: action.workspace,
          openClaimId: null,
          lastSection: { ...ui.lastSection, [action.workspace]: action.section },
        },
      }
    case 'selectSource':
      return { ...state, ui: { ...ui, selectedSourceId: action.id, highlightPassageId: action.passageId ?? null } }
    case 'setSearch':
      return { ...state, ui: { ...ui, search: action.value } }
    case 'toggleCompare': {
      const selected = ui.compareIds.includes(action.id)
      if (!selected && ui.compareIds.length >= 4) return state
      const compareIds = selected ? ui.compareIds.filter((id) => id !== action.id) : [...ui.compareIds, action.id]
      return { ...state, ui: { ...ui, compareIds } }
    }
    case 'setQueueFilter':
      return { ...state, ui: { ...ui, queueFilter: action.filter } }
    case 'setProposalDraft':
      return { ...state, ui: { ...ui, proposalDraft: { ...ui.proposalDraft, ...action.draft } } }
    case 'submitProposal': {
      const draft = ui.proposalDraft
      if (Object.keys(validateProposal(draft)).length > 0 || !draft.predicate) return state
      const at = nowIso()
      const id = formatId('REL', state.counters.claim + 1)
      const claim: Claim = {
        id,
        subjectId: draft.subjectId,
        predicate: draft.predicate,
        objectId: draft.objectId,
        sourceId: draft.sourceId,
        passageId: draft.passageId,
        interpretation: draft.interpretation.trim(),
        limitation: draft.limitation.trim(),
        status: 'proposed',
        version: 1,
        createdAt: at,
        previousVersions: [],
        reviews: [],
      }
      const event = addEvent(
        state,
        { kind: 'proposal', title: `${id} submitted for review`, detail: `${claimSummary(claim)} · ${claim.sourceId} ${claim.passageId}`, claimId: id },
        at,
      )
      return {
        ...state,
        claims: [...state.claims, claim],
        history: event.history,
        counters: { ...event.counters, claim: state.counters.claim + 1 },
        ui: { ...ui, proposalDraft: { ...emptyProposal } },
      }
    }
    case 'setNoteDraft':
      return { ...state, ui: { ...ui, noteDraft: { ...ui.noteDraft, ...action.draft } } }
    case 'saveNote': {
      const { sourceId, text } = ui.noteDraft
      if (!sourceById(sourceId) || !text.trim()) return state
      const at = nowIso()
      const id = formatId('NOTE', state.counters.note + 1)
      const event = addEvent(
        state,
        { kind: 'note', title: `${id} saved`, detail: `Research note linked to ${sourceId}`, sourceId },
        at,
      )
      return {
        ...state,
        notes: [{ id, sourceId, text: text.trim(), createdAt: at }, ...state.notes],
        history: event.history,
        counters: { ...event.counters, note: state.counters.note + 1 },
        ui: { ...ui, noteDraft: { sourceId, text: '' } },
      }
    }
    case 'openClaim':
      return { ...state, ui: { ...ui, openClaimId: action.id } }
    case 'setReviewDraft': {
      const claim = state.claims.find((item) => item.id === action.claimId)
      if (!claim) return state
      const current = ui.reviewDrafts[action.claimId] ?? defaultReviewDraft(claim)
      return {
        ...state,
        ui: { ...ui, reviewDrafts: { ...ui.reviewDrafts, [action.claimId]: { ...current, ...action.draft } } },
      }
    }
    case 'submitReview': {
      const claim = state.claims.find((item) => item.id === action.claimId)
      if (!claim) return state
      const draft = ui.reviewDrafts[claim.id] ?? defaultReviewDraft(claim)
      if (Object.keys(validateReview(draft)).length > 0 || !draft.outcome) return state
      const at = nowIso()
      const reviewNumber = state.counters.review + 1
      const lastChange = claim.reviews[claim.reviews.length - 1]?.at ?? claim.createdAt
      const updated: Claim = {
        ...claim,
        interpretation: draft.interpretation.trim(),
        limitation: draft.limitation.trim(),
        status: draft.outcome,
        version: claim.version + 1,
        previousVersions: [
          ...claim.previousVersions,
          {
            version: claim.version,
            interpretation: claim.interpretation,
            limitation: claim.limitation,
            status: claim.status,
            recordedAt: lastChange,
          },
        ],
        reviews: [
          ...claim.reviews,
          {
            id: formatId('REV', reviewNumber),
            at,
            priorVersion: claim.version,
            priorInterpretation: claim.interpretation,
            priorLimitation: claim.limitation,
            priorStatus: claim.status,
            outcome: draft.outcome,
            rationale: draft.rationale.trim(),
            newVersion: claim.version + 1,
          },
        ],
      }
      const event = addEvent(
        state,
        {
          kind: 'review',
          title: `${claim.id} ${outcomeLabels[draft.outcome].toLowerCase()} · version ${updated.version}`,
          detail: `${claimSummary(claim)} · Rationale: ${draft.rationale.trim()}`,
          claimId: claim.id,
        },
        at,
      )
      const reviewDrafts = { ...ui.reviewDrafts }
      delete reviewDrafts[claim.id]
      return {
        ...state,
        claims: state.claims.map((item) => (item.id === claim.id ? updated : item)),
        history: event.history,
        counters: { ...event.counters, review: reviewNumber },
        ui: { ...ui, reviewDrafts },
      }
    }
    default:
      return state
  }
}

export function researchExport(state: ResearchState) {
  return {
    exportedAt: nowIso(),
    domain: 'research',
    notice: 'Synthetic research collection — all studies, passages and findings are fictional teaching examples.',
    sources: researchSources,
    concepts,
    predicates,
    claims: state.claims,
    reviewHistory: state.history,
    notes: state.notes,
  }
}
